import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import type { ActiveHours, PaceSettings, PeerType, TargetFilter } from "@tg/shared";
import { PEER_TYPES } from "@tg/shared";
import { api, getAppToken } from "../lib/api";
import { useDraft, useTags } from "../lib/hooks";
import { sendController } from "../engine/SendController";
import { useEngineStore } from "../store/useEngineStore";
import { useJobStore } from "../store/useJobStore";
import { Badge, Banner, Button, Card, Input, cn } from "../ui";

const typeLabel: Record<PeerType, string> = {
  group: "Grupos",
  supergroup: "Supergrupos",
  channel: "Canales",
  user: "Usuarios (DM)",
  bot: "Bots",
};

export function SendMonitor() {
  const connected = useEngineStore((s) => s.phase) === "connected";
  const draftQ = useDraft();
  const tagsQ = useTags();
  const job = useJobStore();

  // resume detection
  const [resumeChecked, setResumeChecked] = useState(false);
  useEffect(() => {
    if (!getAppToken() || resumeChecked) return;
    setResumeChecked(true);
    api.activeBroadcast().then(async (b) => {
      if (b && (b.status === "running" || b.status === "paused" || b.status === "peer_flood_halt")) {
        job.setJobId(b.id);
        job.applyProgress(await api.progress(b.id));
      }
    }).catch(() => {});
  }, [resumeChecked, job]);

  // periodic progress refresh while a job is bound
  useEffect(() => {
    if (!job.jobId) return;
    const id = setInterval(async () => {
      try {
        job.applyProgress(await api.progress(job.jobId!));
      } catch {
        /* ignore */
      }
    }, 5000);
    return () => clearInterval(id);
  }, [job.jobId]);

  if (job.jobId) return <Cockpit />;
  return (
    <Preflight
      connected={connected}
      hasImage={(draftQ.data?.media.length ?? 0) > 0}
      tags={tagsQ.data ?? []}
    />
  );
}

// ---------- Pre-flight ----------
function Preflight({
  connected,
  hasImage,
  tags,
}: {
  connected: boolean;
  hasImage: boolean;
  tags: { id: number; name: string; color: string }[];
}) {
  const [mode, setMode] = useState<"all" | "name" | "tags">("all");
  const [q, setQ] = useState("");
  const [tagIds, setTagIds] = useState<number[]>([]);
  const [includeTypes, setIncludeTypes] = useState<Set<PeerType>>(new Set());
  const [minDelay, setMinDelay] = useState(30);
  const [maxDelay, setMaxDelay] = useState(90);
  const [dailyCap, setDailyCap] = useState(1000);
  const [pauseEveryN, setPauseEveryN] = useState<number | "">("");
  const [pauseSecs, setPauseSecs] = useState<number | "">("");
  const [useWindow, setUseWindow] = useState(false);
  const [startHour, setStartHour] = useState(9);
  const [endHour, setEndHour] = useState(22);
  const [scheduledStart, setScheduledStart] = useState("");
  const [showRisk, setShowRisk] = useState(false);
  const [starting, setStarting] = useState(false);

  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const filter: TargetFilter = useMemo(
    () => ({
      mode,
      q: mode === "name" ? q.trim() || undefined : undefined,
      tagIds: mode === "tags" ? tagIds : undefined,
      tagMode: "any",
      includeTypes: includeTypes.size ? [...includeTypes] : undefined,
      activeOnly: true,
      canSendOnly: false,
    }),
    [mode, q, tagIds, includeTypes],
  );

  const countQ = useQuery({
    queryKey: ["count", filter],
    enabled: !!getAppToken(),
    queryFn: () => {
      const p = new URLSearchParams({ activeOnly: "true" });
      if (filter.q) p.set("q", filter.q);
      if (filter.includeTypes?.length) p.set("types", filter.includeTypes.join(","));
      if (filter.tagIds?.length) p.set("tagIds", filter.tagIds.join(","));
      return api.groupsCount(p.toString());
    },
  });

  const count = countQ.data?.count ?? 0;
  const estDays = dailyCap > 0 ? Math.ceil(count / dailyCap) : 0;
  const avgDelay = (minDelay + maxDelay) / 2;
  const estHours = (count * avgDelay) / 3600;
  const includesDM = includeTypes.has("user");

  const pace = (): PaceSettings => ({
    minDelayS: minDelay,
    maxDelayS: maxDelay,
    dailyCap,
    pauseEveryN: pauseEveryN === "" ? null : pauseEveryN,
    pauseSecs: pauseSecs === "" ? null : pauseSecs,
    scheduledStartAt: scheduledStart ? new Date(scheduledStart).getTime() : null,
    activeHours: useWindow ? ({ startHour, endHour, tz } as ActiveHours) : null,
  });

  async function confirmAndStart() {
    setStarting(true);
    try {
      const { id } = await api.createBroadcast({ draftId: 1, targetFilter: filter, pace: pace() });
      await api.materialize(id);
      await api.riskAck(id);
      setShowRisk(false);
      await sendController.start(id);
    } finally {
      setStarting(false);
    }
  }

  const canStart = connected && hasImage && count > 0;

  return (
    <div className="space-y-4">
      <h1 className="text-lg font-semibold">Enviar</h1>
      {!connected && <Banner kind="warn">Inicia sesión en Configuración primero.</Banner>}
      {!hasImage && <Banner kind="warn">Sube una imagen y texto en Redactar antes de enviar.</Banner>}

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card className="space-y-3">
          <h2 className="text-sm font-medium text-slate-300">Destino</h2>
          <div className="flex gap-2">
            {(["all", "name", "tags"] as const).map((m) => (
              <button
                key={m}
                onClick={() => setMode(m)}
                className={cn(
                  "rounded-md px-3 py-1.5 text-xs",
                  mode === m ? "bg-sky-600 text-white" : "bg-slate-800 text-slate-300",
                )}
              >
                {m === "all" ? "Todos" : m === "name" ? "Por nombre" : "Por tags"}
              </button>
            ))}
          </div>
          {mode === "name" && (
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Contiene en el nombre…" />
          )}
          {mode === "tags" && (
            <div className="flex flex-wrap gap-2">
              {tags.map((t) => (
                <button
                  key={t.id}
                  onClick={() =>
                    setTagIds((prev) => (prev.includes(t.id) ? prev.filter((x) => x !== t.id) : [...prev, t.id]))
                  }
                  className={cn(
                    "rounded-full px-3 py-1 text-xs",
                    tagIds.includes(t.id) ? "ring-2 ring-sky-400" : "opacity-70",
                  )}
                  style={{ backgroundColor: t.color, color: "#fff" }}
                >
                  {t.name}
                </button>
              ))}
            </div>
          )}
          <div>
            <p className="mb-1 text-xs text-slate-400">Tipos (vacío = todos)</p>
            <div className="flex flex-wrap gap-2">
              {PEER_TYPES.map((pt) => (
                <button
                  key={pt}
                  onClick={() =>
                    setIncludeTypes((prev) => {
                      const n = new Set(prev);
                      n.has(pt) ? n.delete(pt) : n.add(pt);
                      return n;
                    })
                  }
                  className={cn(
                    "rounded-full border px-3 py-1 text-xs",
                    includeTypes.has(pt) ? "border-sky-500 bg-sky-950 text-sky-200" : "border-slate-700 text-slate-400",
                  )}
                >
                  {typeLabel[pt]}
                </button>
              ))}
            </div>
          </div>
          {includesDM && (
            <Banner kind="danger">
              ⚠ Incluir usuarios (DM) dispara el antispam. Es el mayor riesgo de baneo.
            </Banner>
          )}
          <div className="text-sm text-slate-300">
            Destinatarios: <strong>{count.toLocaleString()}</strong>
          </div>
        </Card>

        <Card className="space-y-3">
          <h2 className="text-sm font-medium text-slate-300">Ritmo y programación</h2>
          <div className="grid grid-cols-2 gap-2 text-xs">
            <label className="space-y-1">
              <span className="text-slate-400">Delay mín (s)</span>
              <Input type="number" value={minDelay} onChange={(e) => setMinDelay(+e.target.value)} />
            </label>
            <label className="space-y-1">
              <span className="text-slate-400">Delay máx (s)</span>
              <Input type="number" value={maxDelay} onChange={(e) => setMaxDelay(+e.target.value)} />
            </label>
            <label className="space-y-1">
              <span className="text-slate-400">Tope diario</span>
              <Input type="number" value={dailyCap} onChange={(e) => setDailyCap(+e.target.value)} />
            </label>
            <label className="space-y-1">
              <span className="text-slate-400">Pausa cada N</span>
              <Input type="number" value={pauseEveryN} onChange={(e) => setPauseEveryN(e.target.value ? +e.target.value : "")} />
            </label>
            <label className="space-y-1">
              <span className="text-slate-400">Duración pausa (s)</span>
              <Input type="number" value={pauseSecs} onChange={(e) => setPauseSecs(e.target.value ? +e.target.value : "")} />
            </label>
            <label className="space-y-1">
              <span className="text-slate-400">Inicio programado</span>
              <Input type="datetime-local" value={scheduledStart} onChange={(e) => setScheduledStart(e.target.value)} />
            </label>
          </div>
          <label className="flex items-center gap-2 text-xs text-slate-400">
            <input type="checkbox" checked={useWindow} onChange={(e) => setUseWindow(e.target.checked)} />
            Limitar a franja horaria
          </label>
          {useWindow && (
            <div className="flex items-center gap-2 text-xs">
              <Input type="number" value={startHour} onChange={(e) => setStartHour(+e.target.value)} className="w-20" />
              <span className="text-slate-400">a</span>
              <Input type="number" value={endHour} onChange={(e) => setEndHour(+e.target.value)} className="w-20" />
              <span className="text-slate-500">{tz}</span>
            </div>
          )}
          <Banner kind="info">
            ≈ {estDays} día(s) a {dailyCap}/día · ~{estHours.toFixed(1)} h de envío activo
          </Banner>
        </Card>
      </div>

      <div className="flex justify-end">
        <Button disabled={!canStart || starting} onClick={() => setShowRisk(true)}>
          Revisar y enviar →
        </Button>
      </div>

      {showRisk && (
        <RiskGate
          count={count}
          includesDM={includesDM}
          onCancel={() => setShowRisk(false)}
          onConfirm={confirmAndStart}
          busy={starting}
        />
      )}
    </div>
  );
}

// ---------- Risk gate ----------
function RiskGate({
  count,
  includesDM,
  onCancel,
  onConfirm,
  busy,
}: {
  count: number;
  includesDM: boolean;
  onCancel: () => void;
  onConfirm: () => void;
  busy: boolean;
}) {
  const [c1, setC1] = useState(false);
  const [c2, setC2] = useState(false);
  const [c3, setC3] = useState(false);
  const [typed, setTyped] = useState("");
  const ready = c1 && c2 && c3 && typed.trim().toUpperCase() === "ENTIENDO";

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
      <Card className="max-w-lg space-y-3">
        <h2 className="text-base font-semibold text-red-300">Aviso de riesgo</h2>
        <p className="text-sm text-slate-300">
          Vas a enviar a <strong>{count.toLocaleString()}</strong> destinos desde tu cuenta personal.
        </p>
        <ul className="list-disc space-y-1 pl-5 text-xs text-slate-400">
          <li>Telegram puede marcar la cuenta como spam (PEER_FLOOD) y limitarla o banear el número permanentemente.</li>
          <li>El techo "seguro" reportado por la comunidad es ~40–100/día; más allá es riesgo creciente.</li>
          {includesDM && <li className="text-red-300">Has incluido DMs: es el mayor riesgo de todos.</li>}
        </ul>
        {[
          [c1, setC1, "Entiendo que mi cuenta puede ser limitada o baneada."],
          [c2, setC2, "Usaré un ritmo prudente y variación (spintax)."],
          [c3, setC3, "Asumo la responsabilidad de este envío."],
        ].map(([v, set, label], i) => (
          <label key={i} className="flex items-start gap-2 text-xs text-slate-300">
            <input type="checkbox" checked={v as boolean} onChange={(e) => (set as (b: boolean) => void)(e.target.checked)} />
            {label as string}
          </label>
        ))}
        <Input value={typed} onChange={(e) => setTyped(e.target.value)} placeholder='Escribe "ENTIENDO"' />
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onCancel}>Cancelar</Button>
          <Button variant="danger" disabled={!ready || busy} onClick={onConfirm}>
            {busy ? "Iniciando…" : "Empezar envío"}
          </Button>
        </div>
      </Card>
    </div>
  );
}

// ---------- Active cockpit ----------
function Cockpit() {
  const job = useJobStore();
  const pct = job.total > 0 ? Math.round(((job.sent + job.skipped + job.failed) / job.total) * 100) : 0;
  const [showEdit, setShowEdit] = useState(false);
  const [editing, setEditing] = useState(false);

  async function startEdit() {
    const orig = job.jobId;
    if (!orig) return;
    setEditing(true);
    try {
      const { id } = await api.createBroadcast({
        draftId: 1,
        mode: "edit",
        editOf: orig,
        targetFilter: { mode: "all" },
        pace: { minDelayS: 5, maxDelayS: 15, dailyCap: 5000 },
      });
      await api.materialize(id);
      await api.riskAck(id);
      setShowEdit(false);
      useJobStore.getState().setJobId(id);
      await sendController.start(id);
    } finally {
      setEditing(false);
    }
  }

  const canEdit = job.sent > 0 && job.status !== "running";

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-semibold">Envío</h1>
        <Badge color={statusColor(job.status)}>{job.status}</Badge>
      </div>

      {job.banner && (
        <Banner kind={job.banner.kind === "peerflood" || job.banner.kind === "auth" ? "danger" : "warn"}>
          {job.banner.text}
        </Banner>
      )}

      <Card className="space-y-3">
        <div className="h-3 w-full overflow-hidden rounded bg-slate-800">
          <div className="h-full bg-sky-500" style={{ width: `${pct}%` }} />
        </div>
        <div className="grid grid-cols-3 gap-2 text-center text-sm sm:grid-cols-6">
          <Stat label="Total" value={job.total} />
          <Stat label="Enviados" value={job.sent} />
          <Stat label="Saltados" value={job.skipped} />
          <Stat label="Fallidos" value={job.failed} />
          <Stat label="Pend." value={job.pending} />
          <Stat label={`Hoy/${job.dailyCap}`} value={job.todaySent} />
        </div>
        {job.current && (
          <p className="text-xs text-slate-400">
            Enviando: {job.current.title}
            {job.current.nextDelayMs ? ` · siguiente en ${Math.round(job.current.nextDelayMs / 1000)}s` : ""}
          </p>
        )}
        <div className="flex gap-2">
          {job.status === "running" ? (
            <Button variant="secondary" onClick={() => sendController.pause()}>Pausar</Button>
          ) : (
            <Button onClick={() => job.jobId && sendController.start(job.jobId)}>
              {job.status === "completed" ? "Reanudar/Reintentar" : "Reanudar"}
            </Button>
          )}
          <Button variant="danger" onClick={() => sendController.stop()}>Detener</Button>
          {canEdit && (
            <Button variant="ghost" onClick={() => setShowEdit(true)}>
              ✏️ Editar enviados
            </Button>
          )}
        </div>
      </Card>

      {showEdit && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-4">
          <Card className="max-w-lg space-y-3">
            <h2 className="text-base font-semibold text-amber-300">Editar mensajes ya enviados</h2>
            <p className="text-sm text-slate-300">
              Se aplicará el <strong>contenido actual de Redactar</strong> (texto e imágenes) a los{" "}
              {job.sent.toLocaleString()} mensajes ya enviados de esta campaña, al mismo ritmo controlado.
            </p>
            <ul className="list-disc space-y-1 pl-5 text-xs text-slate-400">
              <li>Las imágenes solo se sustituyen si el nº coincide con el original; si cambió, solo se edita el texto (se marca <code>EDIT_COUNT_MISMATCH</code>).</li>
              <li>Pasadas ~48 h Telegram puede no permitir editar algunos mensajes (<code>EDIT_TIME_EXPIRED</code>); esos se reportan y el resto continúa.</li>
            </ul>
            <div className="flex justify-end gap-2">
              <Button variant="ghost" onClick={() => setShowEdit(false)}>Cancelar</Button>
              <Button disabled={editing} onClick={startEdit}>
                {editing ? "Iniciando…" : "Editar ahora"}
              </Button>
            </div>
          </Card>
        </div>
      )}

      <Card className="p-0">
        <div className="border-b border-slate-800 px-3 py-2 text-xs text-slate-400">Registro</div>
        <div className="max-h-[40vh] overflow-auto">
          {job.log.length === 0 && <p className="p-3 text-xs text-slate-500">Sin eventos todavía.</p>}
          {job.log.map((e, i) => (
            <div key={i} className="flex items-center gap-2 border-b border-slate-900 px-3 py-1.5 text-xs">
              <span className="w-16 text-slate-600">{new Date(e.ts).toLocaleTimeString()}</span>
              <span className={cn("w-16", resultColor(e.result))}>{e.result}</span>
              <span className="flex-1 truncate text-slate-300">{e.title}</span>
              <span className="truncate text-slate-500">{e.detail}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

function Stat({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded bg-slate-800 p-2">
      <div className="text-lg font-semibold text-slate-100">{value.toLocaleString()}</div>
      <div className="text-xs text-slate-500">{label}</div>
    </div>
  );
}

function statusColor(s: string): string {
  if (s === "running") return "#0284c7";
  if (s === "completed") return "#059669";
  if (s === "peer_flood_halt") return "#dc2626";
  if (s === "paused") return "#d97706";
  return "#475569";
}
function resultColor(r: string): string {
  if (r === "sent") return "text-emerald-400";
  if (r === "failed") return "text-red-400";
  if (r === "skipped") return "text-amber-400";
  if (r === "deferred") return "text-sky-400";
  return "text-slate-400";
}
